import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { apiJson } from './api';
import { useSEO } from './useSEO';

const TopicList = () => {
  const { user, token, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [progress, setProgress] = useState({});
  const [topicScores, setTopicScores] = useState({}); // { [topicId]: { attempts:[], bestPercent, latestPercent, attemptCount, paperCount } }
  const [status, setStatus] = useState(null);
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState(() => new Set());
  const [subjectSaving, setSubjectSaving] = useState(false);
  const [levelChoice, setLevelChoice] = useState('');
  const [subjectChoice, setSubjectChoice] = useState('');
  const [boardChoice, setBoardChoice] = useState('');

  useSEO({
    title: 'Topic tracker',
    description: 'Track your past-paper and question-by-topic progress for GCSE and A-Level subjects.',
    canonicalPath: '/topics'
  });

  useEffect(() => {
    if (loading) return;
    if (!user || !token) {
      navigate('/login');
      return;
    }
    let cancelled = false;
    setStatus(null);
    apiJson('/api/topics')
      .then((res) => {
        if (cancelled) return;
        setGroups(Array.isArray(res?.groups) ? res.groups : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus(err.message || 'Failed to load topics');
      });
    return () => { cancelled = true; };
  }, [loading, user, token, navigate]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    apiJson('/api/topic-progress', { token })
      .then((res) => {
        if (cancelled) return;
        const map = {};
        (res?.progress || []).forEach((p) => {
          if (p?.topicId) {
            map[p.topicId] = {
              completed: !!p.completed,
              confidence: p.confidence === null || p.confidence === undefined ? null : Number(p.confidence),
              lastAttempted: p.lastAttempted || null,
            };
          }
        });
        setProgress(map);
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus(err.message || 'Failed to load your progress');
      });
    return () => { cancelled = true; };
  }, [token]);

  // Pull per-topic scores derived from the user's attempts on
  // "Questions By Topic" papers (joined via topicId on the backend).
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    apiJson('/api/topic-scores', { token })
      .then((res) => {
        if (cancelled) return;
        setTopicScores(res?.scores && typeof res.scores === 'object' ? res.scores : {});
      })
      .catch(() => { /* non-fatal: tracker still works without scores */ });
    return () => { cancelled = true; };
  }, [token]);

  // Load saved subject selections per user from server
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    apiJson('/api/topic-subjects', { token })
      .then((res) => {
        if (cancelled) return;
        const arr = Array.isArray(res?.subjects) ? res.subjects : [];
        setSelectedGroups(new Set(arr));
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus(err.message || 'Failed to load subjects');
      });
    return () => { cancelled = true; };
  }, [token]);

  const persistSubjects = async (nextSet, prevSet) => {
    setSubjectSaving(true);
    try {
      await apiJson('/api/topic-subjects', {
        method: 'POST',
        token,
        body: { subjects: Array.from(nextSet) },
      });
    } catch (err) {
      setStatus(err.message || 'Failed to save subjects');
      setSelectedGroups(prevSet);
    } finally {
      setSubjectSaving(false);
    }
  };


  const clearSelection = () => {
    setSelectedGroups((prev) => {
      const prevSet = new Set(prev);
      const empty = new Set();
      persistSubjects(empty, prevSet);
      return empty;
    });
  };

  const normalizedFilter = filter.trim().toLowerCase();

  const filteredGroups = useMemo(() => {
    const selectedIds = selectedGroups;
    const applyText = (g) => ({
      ...g,
      topics: (g.topics || []).filter((t) => t.name.toLowerCase().includes(normalizedFilter)),
    });

    if (selectedIds.size === 0) return [];

    const base = groups
      .filter((g) => selectedIds.has(g.id))
      .map(applyText)
      .filter((g) => g.topics && g.topics.length);

    return base;
  }, [groups, normalizedFilter, selectedGroups]);


  const levelOptions = useMemo(() => Array.from(new Set(groups.map((g) => g.level))), [groups]);
  const subjectOptions = useMemo(
    () => Array.from(new Set(groups.filter((g) => !levelChoice || g.level === levelChoice).map((g) => g.subject))),
    [groups, levelChoice]
  );
  const boardOptions = useMemo(
    () =>
      Array.from(
        new Set(
          groups
            .filter((g) => (!levelChoice || g.level === levelChoice) && (!subjectChoice || g.subject === subjectChoice))
            .map((g) => g.board)
        )
      ),
    [groups, levelChoice, subjectChoice]
  );

  useEffect(() => {
    if (!levelChoice && levelOptions.length) {
      setLevelChoice(levelOptions[0]);
    }
  }, [levelOptions, levelChoice]);

  useEffect(() => {
    if (!subjectChoice && subjectOptions.length) {
      setSubjectChoice(subjectOptions[0]);
    }
  }, [subjectOptions, subjectChoice]);

  useEffect(() => {
    if (!boardChoice && boardOptions.length) {
      setBoardChoice(boardOptions[0]);
    }
  }, [boardOptions, boardChoice]);

  const addSubject = () => {
    const match = groups.find(
      (g) =>
        (!levelChoice || g.level === levelChoice) &&
        (!subjectChoice || g.subject === subjectChoice) &&
        (!boardChoice || g.board === boardChoice)
    );
    if (!match) {
      setStatus('No matching subject/board found in resources');
      return;
    }
    setSelectedGroups((prev) => {
      const prevSet = new Set(prev);
      const next = new Set(prev);
      next.add(match.id);
      persistSubjects(next, prevSet);
      return next;
    });
  };

  const handleSignOut = () => {
    logout();
    navigate('/login');
  };

  const updateTopicProgress = async (topicId, patch) => {
    const prevEntry = progress[topicId] || {};
    const merged = { ...prevEntry, ...patch };
    setProgress((prev) => ({ ...prev, [topicId]: merged }));
    setSaving(true);
    try {
      const payload = {
        topicId,
        completed: patch.completed !== undefined ? patch.completed : prevEntry.completed || false,
        confidence: patch.confidence !== undefined ? patch.confidence : prevEntry.confidence,
        lastAttempted: patch.lastAttempted !== undefined ? patch.lastAttempted : prevEntry.lastAttempted,
      };
      await apiJson('/api/topic-progress', {
        method: 'POST',
        token,
        body: { items: [payload] },
      });
    } catch (err) {
      setStatus(err.message || 'Failed to save');
      setProgress((prev) => ({ ...prev, [topicId]: prevEntry }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tt-page">
      <header className="tt-header">
        <div>
          <h1>Topic tracker</h1>
          <p className="tt-sub">
            Tick off topics as you work through them. Scores and completion are pulled
            automatically from your past-paper attempts.
          </p>
        </div>
        <div className="tt-header-actions">
          <button className="tt-btn" onClick={() => navigate('/')}>← Resources</button>
          <button className="tt-btn danger" onClick={handleSignOut}>Sign out</button>
        </div>
      </header>

      <section className="tt-toolbar">
        <div className="tt-toolbar-row">
          <div className="tt-field">
            <label>Tier</label>
            <select
              value={levelChoice}
              onChange={(e) => { setLevelChoice(e.target.value); setSubjectChoice(''); setBoardChoice(''); }}
            >
              {levelOptions.map((lvl) => <option key={lvl} value={lvl}>{lvl}</option>)}
            </select>
          </div>
          <div className="tt-field">
            <label>Subject</label>
            <select
              value={subjectChoice}
              onChange={(e) => { setSubjectChoice(e.target.value); setBoardChoice(''); }}
            >
              {subjectOptions.map((subj) => <option key={subj} value={subj}>{subj}</option>)}
            </select>
          </div>
          <div className="tt-field">
            <label>Exam board</label>
            <select value={boardChoice} onChange={(e) => setBoardChoice(e.target.value)}>
              {boardOptions.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="tt-field">
            <label>&nbsp;</label>
            <button
              className="tt-btn primary"
              onClick={addSubject}
              disabled={subjectSaving || !levelChoice || !subjectChoice || !boardChoice}
            >
              + Add tracker
            </button>
          </div>
        </div>
        <div className="tt-search">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search topics…"
          />
          {selectedGroups.size > 0 && (
            <button className="tt-btn" onClick={clearSelection}>Clear all</button>
          )}
        </div>
      </section>

      {status && <div className="tt-status error">{status}</div>}
      {(saving || subjectSaving) && <div className="tt-status saving">Saving…</div>}

      {filteredGroups.length === 0 && (
        <div className="tt-empty">
          {selectedGroups.size === 0
            ? 'No subjects yet — pick a tier, subject and board above and add your first tracker.'
            : 'No topics match your search.'}
        </div>
      )}

      {filteredGroups.map((group) => {
        const totalTopics = group.topics?.length || 0;
        const completedTopics = (group.topics || []).filter((t) => progress[t.id]?.completed).length;
        const groupPct = totalTopics ? Math.round((completedTopics / totalTopics) * 100) : 0;
        return (
          <section key={group.id} className="tt-group">
            <header className="tt-group-head">
              <div className="tt-group-title">
                <h3>{group.subject}</h3>
                <span className="tt-tag">{group.level}</span>
                {group.board && <span className="tt-tag">{group.board}</span>}
              </div>
              <div className="tt-group-meta">
                <span>{completedTopics}/{totalTopics} done</span>
                <div className="tt-mini-bar"><span style={{ width: `${groupPct}%` }} /></div>
              </div>
            </header>

            <div className="tt-row tt-row-head">
              <span>Topic</span>
              <span>Best score</span>
              <span>Completion</span>
              <span>Last attempted</span>
              <span>Resources</span>
            </div>

            {(group.topics || []).map((topic) => {
              const prog = progress[topic.id] || {};
              const checked = !!prog.completed;
              const lastAttempted = prog.lastAttempted ? prog.lastAttempted.slice(0, 10) : '';
              const files = topic.files || [];
              const scoreInfo = topicScores[topic.id];
              const latestAttempt = scoreInfo?.attempts?.[0] || null;
              const totalPapers = files.length;
              const attemptedPapers = Math.min(scoreInfo?.paperCount || 0, totalPapers);
              const completionPct = checked
                ? 100
                : totalPapers > 0
                  ? Math.round((attemptedPapers / totalPapers) * 100)
                  : 0;
              const barClass = completionPct >= 100 ? 'full' : completionPct >= 50 ? 'mid' : completionPct > 0 ? 'low' : 'zero';
              const pillClass = scoreInfo?.bestPercent >= 70 ? 'good' : scoreInfo?.bestPercent >= 50 ? 'mid' : 'low';

              return (
                <div key={topic.id} className="tt-row">
                  <div className="tt-topic-cell" data-label="Topic">
                    <input
                      type="checkbox"
                      className="tt-checkbox"
                      checked={checked}
                      onChange={(e) => updateTopicProgress(topic.id, { completed: e.target.checked })}
                    />
                    <div>
                      <div className="tt-topic-name">{topic.name}</div>
                      <div className="tt-topic-files">
                        {files.length} question file{files.length === 1 ? '' : 's'}
                      </div>
                    </div>
                  </div>

                  <div data-label="Best score">
                    {scoreInfo && scoreInfo.bestPercent !== null ? (
                      <div>
                        <span className={`tt-score-pill ${pillClass}`} title={`Best of ${scoreInfo.attemptCount} attempt${scoreInfo.attemptCount === 1 ? '' : 's'}`}>
                          {scoreInfo.bestPercent}%
                        </span>
                        <div className="tt-score-meta">
                          {scoreInfo.attemptCount} attempt{scoreInfo.attemptCount === 1 ? '' : 's'}
                          {latestAttempt && (
                            <> · <a href={`/viewer/${latestAttempt.paperId}`} target="_blank" rel="noreferrer" title="Open latest attempt">
                              latest {scoreInfo.latestPercent !== null ? `${scoreInfo.latestPercent}%` : ''}
                            </a></>
                          )}
                        </div>
                      </div>
                    ) : scoreInfo && scoreInfo.attemptCount > 0 ? (
                      <div className="tt-score-empty">{scoreInfo.attemptCount} attempt{scoreInfo.attemptCount === 1 ? '' : 's'} · no score</div>
                    ) : (
                      <div className="tt-score-empty">—</div>
                    )}
                  </div>

                  <div data-label="Completion">
                    <div className="tt-progress">
                      <div
                        className={`tt-bar ${barClass}`}
                        title={totalPapers ? `${attemptedPapers}/${totalPapers} attempted${checked ? ' · marked complete' : ''}` : 'No question files yet'}
                      >
                        <span style={{ width: `${completionPct}%` }} />
                      </div>
                      <div className="tt-pct">{completionPct}%</div>
                    </div>
                  </div>

                  <div data-label="Last attempted">
                    <input
                      type="date"
                      className="tt-date"
                      value={lastAttempted}
                      onChange={(e) => updateTopicProgress(topic.id, { lastAttempted: e.target.value || null })}
                    />
                  </div>

                  <div data-label="Resources">
                    {files.length === 0 ? (
                      <span className="tt-resources-empty">—</span>
                    ) : (
                      <div className="tt-resources">
                        {files.slice(0, 3).map((f, i) => (
                          <a key={f.url} href={f.url} target="_blank" rel="noreferrer">
                            File {i + 1}
                          </a>
                        ))}
                        {files.length > 3 && <span className="tt-more">+{files.length - 3}</span>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
};

export default TopicList;
